import { getItem } from '@/modules/inventory/services/itemRepository';
import { getInventoryLocation } from '@/services/inventoryMovements/locationRepository';
import {
  filterMovements,
  insertMovement,
  listMovementsForCompany,
  listMovementsForItem,
  listMovementsForLocation,
} from '@/services/inventoryMovements/movementRepository';
import { applyItemTrackingFromMovement } from '@/services/inventoryMovements/itemTracking';
import type {
  InventoryMovement,
  InventoryMovementInput,
  MovementFilters,
} from '@/services/inventoryMovements/types';
import { TRANSIT_LOCATION_SENTINEL } from '@/services/inventoryMovements/constants';
import {
  publishInventoryAdjustment,
  publishInventoryDiscrepancy,
  publishInventoryMovementCritical,
  publishInventoryTransfer,
} from '@/services/events/inventoryMovementEvents';

export async function createMovement(
  input: InventoryMovementInput,
  createdBy: string,
): Promise<InventoryMovement> {
  const item = await getItem(input.inventoryItemId);
  if (!item) throw new Error('Artículo no encontrado.');

  if (input.fromLocationId) {
    const loc = await getInventoryLocation(input.fromLocationId);
    if (!loc?.active) throw new Error('Ubicación origen inválida.');
  }
  if (input.toLocationId) {
    const loc = await getInventoryLocation(input.toLocationId);
    if (!loc?.active) throw new Error('Ubicación destino inválida.');
  }

  const movement = await insertMovement(input, createdBy);
  await applyItemTrackingFromMovement(
    input.inventoryItemId,
    input.type,
    {
      from: input.fromLocationId ?? item.currentLocationId ?? null,
      to: input.toLocationId ?? null,
    },
    input.quantity,
  );

  void notifyForMovement(movement);
  if (movement.type === 'check_out') {
    void publishInventoryMovementCritical(movement);
  }
  return movement;
}

async function notifyForMovement(movement: InventoryMovement): Promise<void> {
  if (movement.type === 'transfer') {
    void publishInventoryTransfer(movement);
  }
  if (movement.type === 'manual_adjustment') {
    void publishInventoryAdjustment(movement);
  }
  if (movement.type === 'inventory_count' && movement.notes.includes('discrepancia')) {
    void publishInventoryDiscrepancy(movement);
  }
}

export async function getItemMovements(
  itemId: string,
): Promise<InventoryMovement[]> {
  return listMovementsForItem(itemId);
}

export async function getLocationMovements(
  locationId: string,
): Promise<InventoryMovement[]> {
  return listMovementsForLocation(locationId);
}

export async function listMovements(
  filters: MovementFilters = {},
): Promise<InventoryMovement[]> {
  const all = await listMovementsForCompany();
  return filterMovements(all, filters);
}

export type CheckOutParams = {
  inventoryItemId: string;
  fromLocationId?: string | null;
  reservationId?: string | null;
  logisticsOrderId?: string | null;
  notes?: string;
};

export async function createCheckOut(
  params: CheckOutParams,
  createdBy: string,
): Promise<InventoryMovement> {
  const item = await getItem(params.inventoryItemId);
  if (!item) throw new Error('Artículo no encontrado.');
  if (item.status === 'sold' || item.status === 'inactive') {
    throw new Error('El artículo no puede salir en su estado actual.');
  }

  return createMovement(
    {
      inventoryItemId: params.inventoryItemId,
      type: 'check_out',
      fromLocationId: params.fromLocationId ?? item.currentLocationId ?? null,
      toLocationId: null,
      reservationId: params.reservationId ?? null,
      logisticsOrderId: params.logisticsOrderId ?? null,
      notes: params.notes ?? '',
    },
    createdBy,
  );
}

export type CheckInParams = {
  inventoryItemId: string;
  toLocationId: string;
  reservationId?: string | null;
  logisticsOrderId?: string | null;
  notes?: string;
};

export async function createCheckIn(
  params: CheckInParams,
  createdBy: string,
): Promise<InventoryMovement> {
  const item = await getItem(params.inventoryItemId);
  if (!item) throw new Error('Artículo no encontrado.');

  return createMovement(
    {
      inventoryItemId: params.inventoryItemId,
      type: 'check_in',
      fromLocationId: item.currentLocationId ?? null,
      toLocationId: params.toLocationId,
      reservationId: params.reservationId ?? null,
      logisticsOrderId: params.logisticsOrderId ?? null,
      notes: params.notes ?? '',
    },
    createdBy,
  );
}

export type TransferParams = {
  inventoryItemId: string;
  fromLocationId: string;
  toLocationId: string;
  notes?: string;
};

export async function createTransfer(
  params: TransferParams,
  createdBy: string,
): Promise<InventoryMovement> {
  if (params.fromLocationId === params.toLocationId) {
    throw new Error('Origen y destino deben ser distintos.');
  }
  return createMovement(
    {
      inventoryItemId: params.inventoryItemId,
      type: 'transfer',
      fromLocationId: params.fromLocationId,
      toLocationId: params.toLocationId,
      notes: params.notes ?? '',
    },
    createdBy,
  );
}

export type InventoryCountParams = {
  inventoryItemId: string;
  locationId: string;
  expectedQuantity?: number;
  countedQuantity: number;
  notes?: string;
};

export async function createInventoryCount(
  params: InventoryCountParams,
  createdBy: string,
): Promise<InventoryMovement> {
  const item = await getItem(params.inventoryItemId);
  if (!item) throw new Error('Artículo no encontrado.');

  const expected = params.expectedQuantity ?? item.stockQuantity;
  const delta = params.countedQuantity - expected;
  const discrepancy = delta !== 0;
  const notes =
    params.notes?.trim() ||
    (discrepancy
      ? `discrepancia: esperado ${expected}, contado ${params.countedQuantity}`
      : `conteo OK: ${params.countedQuantity}`);

  const movement = await createMovement(
    {
      inventoryItemId: params.inventoryItemId,
      type: 'inventory_count',
      fromLocationId: params.locationId,
      toLocationId: params.locationId,
      quantity: delta,
      notes,
    },
    createdBy,
  );

  if (discrepancy) {
    await createMovement(
      {
        inventoryItemId: params.inventoryItemId,
        type: 'manual_adjustment',
        fromLocationId: params.locationId,
        toLocationId: params.locationId,
        quantity: delta,
        notes: `Ajuste por conteo (${expected} → ${params.countedQuantity})`,
      },
      createdBy,
    );
  }

  return movement;
}

export async function recordMaintenanceMovement(
  itemId: string,
  maintenanceTaskId: string,
  direction: 'out' | 'in',
  kind: 'maintenance' | 'repair',
  createdBy: string,
  toLocationId?: string | null,
): Promise<InventoryMovement> {
  const type =
    direction === 'out'
      ? kind === 'repair'
        ? 'repair_out'
        : 'maintenance_out'
      : kind === 'repair'
        ? 'repair_in'
        : 'maintenance_in';

  const item = await getItem(itemId);
  if (!item) throw new Error('Artículo no encontrado.');

  return createMovement(
    {
      inventoryItemId: itemId,
      type,
      fromLocationId:
        direction === 'out' ? item.currentLocationId ?? null : null,
      toLocationId:
        direction === 'in'
          ? (toLocationId ?? item.currentLocationId ?? null)
          : null,
      maintenanceTaskId,
      notes: '',
    },
    createdBy,
  );
}

export async function recordLogisticsMovement(
  itemId: string,
  logisticsOrderId: string,
  orderType: 'delivery' | 'pickup' | 'transfer',
  createdBy: string,
): Promise<InventoryMovement | null> {
  if (orderType === 'transfer') {
    return null;
  }
  const item = await getItem(itemId);
  if (!item) return null;

  if (orderType === 'delivery') {
    return createMovement(
      {
        inventoryItemId: itemId,
        type: 'check_out',
        fromLocationId: item.currentLocationId ?? null,
        logisticsOrderId,
        notes: 'Salida por entrega logística',
      },
      createdBy,
    );
  }

  const dest = item.currentLocationId;
  if (!dest || dest === TRANSIT_LOCATION_SENTINEL) {
    return null;
  }
  return createMovement(
    {
      inventoryItemId: itemId,
      type: 'check_in',
      fromLocationId: TRANSIT_LOCATION_SENTINEL,
      toLocationId: dest,
      logisticsOrderId,
      notes: 'Retorno por logística',
    },
    createdBy,
  );
}
